import { createApi } from "@reduxjs/toolkit/query/react";
import {
  AgreedAndForwardResponse,
  AllInvoiceDetails,
  ChangeInvoiceStatusRequest,
  ChangeInvoiceStatusResponse,
  CombinedSearchArgs,
  CombinedSearchEmployeeArgs,
  CombinedSearchEmployeeInvoiceEntry,
  CombinedSearchInvoiceEntry,
  EmployeeInvoicesApiResponse,
  EmployeeInvoicesData,
  IApprovalLog,
  IEmpDashboardData,
  IEmployeeData,
  IEmployeeList,
  InvoiceDetailsResponseVendor,
  InvoiceEntriesApiResponse,
  IReportsList,
  LandingPageApiResponse,
  LandingPageData,
  ParkInvoiceRequest,
  ParkInvoiceResponse,
  SoftDeleteRequest,
} from "../../utils/types";
import { baseQueryWithLogout } from "../baseQuery";

export const landingPageApi = createApi({
  reducerPath: "landingPageApi",
  baseQuery: baseQueryWithLogout,
  tagTypes: ["Invoice", "reject", "deleteInvoice", "pullbackinvoice"],
  endpoints: (builder) => ({
    getLandingPageInfo: builder.query<LandingPageData, void>({
      query: () => ({
        url: "/landing_page",
        method: "GET",
      }),
      transformResponse: (response: LandingPageApiResponse, meta, arg) => {
        if (response && response.success && response.data) {
          return response.data; // Returns the content of the 'data' property
        }
        if (!response.success) {
          console.error("Server indicated GET /landing_page was not successful:", response.message);
          throw new Error(response.message || "Request to /landing_page was not successful.");
        }
        console.error("Unexpected response structure from GET /landing_page:", response);
        throw new Error("Invalid data structure received from server.");
      },
    }),
    getInvoiceEntries: builder.query<InvoiceEntriesApiResponse, number | void>({
      query: (page = 1) => ({
        url: `/invoice-entries?page=${page}`,
        method: "GET",
      }),
      transformResponse: (response: InvoiceEntriesApiResponse) => {
        if (response && response.success) {
          return response; // Return full object with pagination
        }
        throw new Error(response.message || "Failed to fetch invoice entries.");
      },
      providesTags: ["deleteInvoice"]
    }),
    searchInvoices: builder.query<CombinedSearchInvoiceEntry[], CombinedSearchArgs>({
      // Or the actual response type from backend
      query: (args) => {
        const params = new URLSearchParams();

        if (args.bts_no) {
          params.append("bts_no", args.bts_no);
        }
        if (args.po_contract) {
          params.append("po_contract", args.po_contract);
        }
        if (args.invoice_status && args.invoice_status.length > 0) {
          // For repeated parameters (e.g., invoice_status=pending&invoice_status=approved)
          args.invoice_status.forEach((status) => params.append("status", status));
        }

        const queryString = params.toString();

        return {
          url: `/combined-search${queryString ? `?${queryString}` : ""}`, // Attach the query string
          method: "GET",
        };
      },
      // You might need a transformResponse if the API wraps the array
      // For example, if the API returns: { data: CombinedSearchInvoiceEntry[], pagination: {...} }
      // transformResponse: (response: { data: CombinedSearchInvoiceEntry[], /* other props */ }) => response.data,
    }),
    handleInvoiceEntry: builder.mutation<any, { payload: any; isUpdate: boolean }>({
      query: (submissionObject) => ({
        url: "create_or_update_invoice_entry",
        method: submissionObject.isUpdate ? "PUT" : "POST",
        body: submissionObject.payload, // Send only the inner 'payload' as the body
      }),
      invalidatesTags: ["Invoice"],
    }),
    getInvoiceAttachment: builder.query<any, any>({
      query: (url) => `/get_invoice_attachment${url}`,
    }),
    refetchApproverData: builder.mutation({
      query: (detailsPayload) => {
        const body = new URLSearchParams();
        for (const key in detailsPayload) {
          if (detailsPayload[key]) {
            body.append(key, detailsPayload[key]);
          }
        }

        return {
          url: "/approver_page_reload",
          method: "POST",
          body: body,
        };
      },
    }),
    getInvoiceDetails: builder.query<InvoiceDetailsResponseVendor, string>({
      query: (invoice_id) => `get_invoice_details?invoice_id=${invoice_id}`,
      providesTags: (result, error, id) => [{ type: "Invoice", id }],
    }),
    softDeleteInvoice: builder.mutation<any, SoftDeleteRequest>({
      query: (body) => ({
        url: "soft-delete-invoice",
        method: "POST",
        body: body,
      }),
      /**
       * Invalidate the 'Invoice' tag. This will automatically trigger a refetch of:
       * 1. `searchInvoices` (because it provides the 'Invoice' tag)
       * 2. `getInvoiceDetails` for the specific invoice (if it's currently being watched)
       * This keeps your UI perfectly in sync after a deletion.
       */
      invalidatesTags: (result, error, args) => [
        { type: "Invoice", id: args.invoice_id },
        { type: "Invoice", id: "LIST" },
        { type: "deleteInvoice" },
      ],
    }),
    uploadTds: builder.mutation<any, any>({
      query: (body) => ({
        url: "/upload-tds",
        method: "POST",
        body: body,
      }),
    }),
    setAllTdsVisible: builder.mutation<any, any>({
      query: (body) => ({
        url: "/set-all-tds-visible?" + body,
        method: "POST",
      }),
    }),
    getEmployeeInvoices: builder.query<EmployeeInvoicesData, any>({
      query: (args) => ({
        url: "/get-invoice-details-employee",
        params: args,
      }),
      transformResponse: (response: EmployeeInvoicesApiResponse) => {
        if (response && response.success) {
          return response.data;
        }
        throw new Error("Failed to load employee invoices.");
      },
      providesTags: (result) =>
        result
          ? [
            ...result.entries.map(({ bts_no }) => ({
              type: "Invoice" as const,
              id: bts_no,
            })),
            { type: "Invoice", id: "LIST" },
          ]
          : [{ type: "Invoice", id: "LIST" }],
    }),
    getGEMInvoices: builder.query<EmployeeInvoicesData, any>({
      query: (args) => ({
        url: "/get-gem-invoices",
        params: args,
      }),

      transformResponse: (response: EmployeeInvoicesApiResponse) => {
        if (response && response.success) {
          return response.data;
        }
        throw new Error("Failed to load employee invoices.");
      },
      providesTags: (result) =>
        result
          ? [
            ...result.entries.map(({ bts_no }) => ({
              type: "Invoice" as const,
              id: bts_no,
            })),
            { type: "Invoice", id: "LIST" },
          ]
          : [{ type: "Invoice", id: "LIST" }],
    }),
    getEmployeePendingInvoices: builder.query<EmployeeInvoicesData, any>({
      query: (args) => ({
        url: "/get-invoice-details-employee-approvalpending",
        method: "GET",
        params: args,
      }),
      transformResponse: (response: EmployeeInvoicesApiResponse) => {
        if (response && response.success) {
          return response.data;
        }
        throw new Error("Failed to load employee invoices.");
      },
      providesTags: (result) =>
        result
          ? [
            ...result.entries.map(({ bts_no }) => ({
              type: "Invoice" as const,
              id: bts_no,
            })),
            { type: "Invoice", id: "LIST" },
          ]
          : [{ type: "Invoice", id: "LIST" }],
    }),
    searchEmployeeInvoices: builder.query<CombinedSearchEmployeeInvoiceEntry[], CombinedSearchEmployeeArgs>({
      query: (args) => {
        const params = { ...args };
        Object.keys(params).forEach((key) => {
          if (!params[key as keyof typeof params]) {
            delete params[key as keyof typeof params];
          }
        });

        return {
          url: "/combined_search_employee",
          params: params,
        };
      },
    }),
    getApprovalPendingSearch: builder.query<any, any>({
      query: (args) => {
        const params = { ...args };
        Object.keys(params).forEach((key) => {
          if (!params[key as keyof typeof params]) {
            delete params[key as keyof typeof params];
          }
        });
        return {
          url: "/filter_approvalpending",
          params: params,
        };
      },
    }),
    getSearchMyRequestSearch: builder.query<any, any>({
      query: (args) => {
        const params = { ...args };
        Object.keys(params).forEach((key) => {
          if (!params[key as keyof typeof params]) {
            delete params[key as keyof typeof params];
          }
        });
        return {
          url: "/filter_myrequest_employee",
          params: params,
        };
      },
    }),
    searchGEMInvoices: builder.query<EmployeeInvoicesData, any>({
      query: (args) => {
        const params = { ...args };
        Object.keys(params).forEach((key) => {
          if (!params[key as keyof typeof params]) {
            delete params[key as keyof typeof params];
          }
        });

        return {
          url: "/get-gem-invoices",
          params: params,
        };
      },
      transformResponse: (response: any) => {
        if (response && response.success) {
          return response.data;
        }
        throw new Error("Failed to load employee invoices.");
      },
    }),
    handleEmployeeInvoiceEntry: builder.mutation<any, any>({
      query: (submissionObject) => ({
        url: "create_invoice_entry",
        method: "POST",
        body: submissionObject.payload, // Send only the inner 'payload' as the body
      }),
      invalidatesTags: ["Invoice"],
    }),
    getPullbackInvoice: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/pullback_invoice",
        method: "POST",
        body: payload, // Send only the inner 'payload' as the body
      }),
      invalidatesTags: ["pullbackinvoice"],
    }),
    getEmployeeInvoiceDetails: builder.query<AllInvoiceDetails, string>({
      // Use a specific type for the argument, like string or number
      query: (invoiceId) => `/get_invoice_details?invoice_id=${invoiceId}`,

      // CORRECT: Queries "provide" tags. Mutations "invalidate" them.
      // This says "The data from this query is associated with the 'Invoice' tag."
      // It also associates it with a specific ID, which is a best practice for granular updates.
      providesTags: (result, error, invoiceId) => [{ type: "Invoice", id: invoiceId }],
    }),
    changeInvoiceStatus: builder.mutation<ChangeInvoiceStatusResponse, ChangeInvoiceStatusRequest>({
      query: (payload) => ({
        url: "/change_invoice_status",
        method: "POST",
        body: payload, // RTK Query will automatically stringify this to JSON
      }),
    }),
    agreedAndForwardInvoice: builder.mutation<AgreedAndForwardResponse, any>({
      query: (payload) => {
        // Construct URLSearchParams for x-www-form-urlencoded
        const body = new URLSearchParams();
        body.append("invoice_id", String(payload?.invoice_id)); // Ensure it's a string
        body.append("approver_name", payload?.approver_name);
        body.append("pernr", payload?.pernr);
        body.append("approver_assigned", payload?.approver_assigned);
        body.append("approverremarks", payload?.approverremarks);
        body.append("emp_name", localStorage.getItem("employee_name"));
        body.append("hnt_value", payload?.hnt_value);

        return {
          url: "/agreedandforward",
          method: "POST",
          body: body, // fetch will set Content-Type to application/x-www-form-urlencoded
        };
      },
      // You can also transform the error if needed:
      // transformErrorResponse: (response: { status: number; data: any }, meta, arg) => {
      //   if (response.status === 404 && response.data?.message === "Invoice not found") {
      //     return { ...response.data, isInvoiceNotFoundError: true }; // Augment error
      //   }
      //   return response.data || { message: "An unknown error occurred" };
      // },
    }),
    parkInvoice: builder.mutation<ParkInvoiceResponse, ParkInvoiceRequest>({
      query: (payload) => ({
        url: "/park_invoice",
        method: "POST",
        body: payload, // RTK Query automatically stringifies this to JSON
      }),
    }),
    parkInvoiceBulk: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/save_zgrn_details_bulk",
        method: "POST",
        body: payload, // RTK Query automatically stringifies this to JSON
      }),
    }),
    getEmployeeList: builder.query<IEmployeeList, void>({
      query: () => ({
        url: "/get_employee_details_employee",
        method: "GET",
      }),
    }),
    getEmployeeListNew: builder.query<{ d: { results: IEmployeeData[] } }, void>({
      query: () => ({
        url: "/get_zdd_employee_data",
        method: "GET",
      }),
    }),
    getApprovalLogs: builder.query<
      {
        logs: IApprovalLog[];
      },
      void
    >({
      query: (id) => ({
        url: `/approval_logs?invoice_id=${id}`,
        method: "GET",
      }),
    }),
    getReports: builder.query<IReportsList, any>({
      query: (args) => ({
        url: "/overview_report_for_employee",
        params: args,
      }),
    }),
    getMyActivityLogs: builder.query<IReportsList, any>({
      query: (args) => ({
        url: "/my_activity_logs",
        params: args,
      }),
      providesTags: ['pullbackinvoice']
    }),
    getBtsReports: builder.query<IReportsList, any>({
      query: (args) => ({
        url: "/api/report_for_employee",
        params: args,
      }),
    }),
    getBtsReportswithSearch: builder.query<IReportsList, any>({
      query: (args) => ({
        url: "/api/report_for_employee",
        params: args,
      }),
    }),
    getRejectInoiceReports: builder.query<IReportsList, any>({
      query: (args) => ({
        url: "/overview_report_for_parked_invoices",
        params: args,
      }),
      providesTags: ["reject"]

    }),

    getHandTData: builder.mutation<IReportsList, any>({
      query: (value) => ({
        url: "/getcontractpoSet?po_contract=" + value,
      }),
    }),
    getEmpDashboardData: builder.query<{ data: IEmpDashboardData }, void>({
      query: () => ({
        url: "/get_location_status_counts",
        method: "GET",
      }),
    }),
    getEmployeeDetailsOData: builder.query<any, any>({
      query: () => ({
        url: "/get_employee_details_odata",
        method: "GET",
      }),
    }),
    getFetchLocation: builder.query<any, any>({
      query: () => ({
        url: "/fetch/location",
        method: "GET",
      }),
    }),
    getAgreedAndForwardMultiple: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/agreedandforwardmultiple",
        method: "POST",
        body: payload, // RTK Query automatically stringifies this to JSON
      }),
      invalidatesTags: ["Invoice"],
    }),
    getRejectMultiple: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/reject_multiple",
        method: "POST",
        body: payload, // RTK Query automatically stringifies this to JSON
      }),
      invalidatesTags: ["reject"],

    }),
    getAddFeedback: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/feedbacks",
        method: "POST",
        body: payload,
      }),
    }),
    getValidateTDS: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/tds/validate-vendors",
        method: "POST",
        body: payload,
      }),
    }),
    getUploadTDSBatchFiles: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/tds/upload-batch",
        method: "POST",
        body: payload,
      }),
    }),
    // ── Invoice Smart Extraction ──────────────────────────────────────────
    uploadForExtraction: builder.mutation<
      {
        success: boolean;
        filename: string;
        fields: Record<string, any>;
        confidence: Record<string, any>;
        usage?: Record<string, any>;
        cost?: Record<string, any>;
        timing?: { seconds: number };
        trace_id?: string;
        source?: string;
      },
      FormData
    >({
      query: (body) => ({
        url: "/extract",
        method: "POST",
        body,
        // Do NOT set Content-Type manually — browser sets multipart boundary automatically
        formData: true,
      }),
    }),
    getExtractionStatus: builder.query<
      { invoice_id: string; status: string; error?: string },
      string
    >({
      query: (id) => `/invoice-extract-status/${id}`,
    }),
    getExtractionResult: builder.query<
      {
        invoice_id: string;
        original_filename: string;
        fields: Record<string, any>;
        confidence: Record<string, any>;
      },
      string
    >({
      query: (id) => `/invoice-extract-result/${id}`,
    }),
    getTredsPaymentsDetails: builder.query<any, any>({
      query: (id) => `/get_tredsvendordetails_data?vendor_code=` + id,
    }),
    getEmployeeSession: builder.mutation({
      query: (payload) => {
        return {
          url: '/auth/employee/session',
          method: "POST",
          body: payload
        };
      },
    }),
    getEmployeeLogout: builder.mutation({
      query: (payload) => {
        return {
          url: '/logout_emp',
          method: "POST",
          body: payload
        };
      },
    }),
    getTredsDetails: builder.query({
      query: (payload) => {
        return {
          url: '/api/tredsinforbts/' + payload,
          method: "GET",
        };
      },
    }),
    getTredsListOdata: builder.query({
      query: (payload) => {
        return {
          url: '/treds_list_odata?po=' + payload,
          method: "GET",
        };
      },
    }),
  }),
});



export const {
  useGetTredsListOdataQuery,
  useGetTredsDetailsQuery,
  useGetEmployeeLogoutMutation,
  useGetEmployeeSessionMutation,
  useGetTredsPaymentsDetailsQuery,
  useGetUploadTDSBatchFilesMutation,
  useGetValidateTDSMutation,
  useGetAddFeedbackMutation,
  useGetSearchMyRequestSearchQuery,
  useGetApprovalPendingSearchQuery,
  useGetPullbackInvoiceMutation,
  useGetMyActivityLogsQuery,
  useGetBtsReportsQuery,
  useGetBtsReportswithSearchQuery,
  useGetRejectMultipleMutation,
  useGetRejectInoiceReportsQuery,
  useGetFetchLocationQuery,
  useGetAgreedAndForwardMultipleMutation,
  useGetEmployeeDetailsODataQuery,
  useGetHandTDataMutation,
  useGetLandingPageInfoQuery,
  useGetInvoiceEntriesQuery,
  useSearchInvoicesQuery,
  useHandleInvoiceEntryMutation,
  useGetInvoiceDetailsQuery,
  useSoftDeleteInvoiceMutation,
  useGetEmployeeInvoicesQuery,
  useGetEmployeePendingInvoicesQuery,
  useSearchEmployeeInvoicesQuery,
  useHandleEmployeeInvoiceEntryMutation,
  useGetEmployeeInvoiceDetailsQuery,
  useChangeInvoiceStatusMutation,
  useAgreedAndForwardInvoiceMutation,
  useParkInvoiceMutation,
  useParkInvoiceBulkMutation,
  useGetEmployeeListQuery,
  useGetEmployeeListNewQuery,
  useGetApprovalLogsQuery,
  useLazyGetInvoiceAttachmentQuery,
  useRefetchApproverDataMutation,
  useUploadTdsMutation,
  useSetAllTdsVisibleMutation,
  useGetReportsQuery,
  useGetEmpDashboardDataQuery,
  useSearchGEMInvoicesQuery,
  useGetGEMInvoicesQuery,
  useUploadForExtractionMutation,
  useLazyGetExtractionStatusQuery,
  useLazyGetExtractionResultQuery,
} = landingPageApi;
